/**
 * An item's itinerary on its own card (core/detail.ts, view `itinerary`), wherever the card shows one (the
 * results, the summary, the ticket): what it needs is an ItineraryOf (itinerary.ts), not where it came from.
 *  - ChangesButton  «1 cambio»: a touch asks the backend for it (show_detail, the same action as the voice,
 *                   and the reply says it), a second touch closes it;
 *  - ItineraryCover the itinerary over the card while a reply keeps it open (ui-kit CoverPanel);
 *  - the legs and, between two, where to change and how long the wait.
 * The button's look (`ButtonLook`) and the itinerary's (`ItineraryLook`) are the card's own: `row` one more line
 * of a card's details, `chip` a chip like the big card's classes, `bar` the old kiosk ticket's bar across its
 * foot; `cards` a card per leg and a pill between (the old kiosk's), `timeline` a line down the stops. The card
 * must be `position: relative`.
 */
import { Fragment } from 'react';
import { useDetail } from '@platform/react/hooks';
import { CoverPanel } from '@platform/ui-kit/CoverPanel';
import { Icon } from '@platform/ui-kit/Icon';
import type { WidgetApi } from '@platform/widgets/contract';
import type { Itinerary, ItineraryChange, ItineraryOf } from './itinerary';
import css from './Itinerary.module.css';
import { changesLabel } from './trip';

/** The detail's name, as the backend's show_detail calls it (spec.yaml). */
export const ITINERARY = 'itinerary';

export type ButtonLook = 'row' | 'chip' | 'bar';
export type ItineraryLook = 'cards' | 'timeline';

type T = (k: string, p?: Record<string, string | number>) => string;

export function ChangesButton({ of, api, t, look, className }: {
  of: ItineraryOf; api: WidgetApi; t: T; look: ButtonLook; className?: string;
}) {
  const { detail, close } = useDetail(of.item);
  const open = detail?.view === ITINERARY;
  const label = changesLabel(of.changes, t);
  return (
    <button type="button" className={`${css.changes} ${css[look]} ${className ?? ''}`}
      aria-expanded={open} disabled={api.readOnly}
      onClick={(e) => {
        e.stopPropagation();
        if (open) close();
        // `chosen` picks the touch's words only (spec.yaml phrases), it is no argument of the action
        else api.press('show_detail', of.chosen ? { idx: of.idx, view: ITINERARY, chosen: true } : { idx: of.idx, view: ITINERARY });
      }}>
      {look === 'chip' ? (
        <>
          <Icon name="next" size={26} />
          <span className={css.line}><b>{label}</b><span aria-hidden>·</span>{t('trains.itinerary.show')}<Icon name="more" size={22} /></span>
        </>
      ) : (
        <>
          {look === 'row' && <Icon name="next" size={20} />}
          <span className={css.label}>
            <b>{label}</b>
            <small>{t('trains.itinerary.show')}{look === 'row' && <Icon name="info" size={14} />}</small>
          </span>
          {look === 'bar' && <Icon name="info" size={28} />}
        </>
      )}
    </button>
  );
}

export function ItineraryCover({ of, t, closeLabel, look, headerClassName }: {
  of: ItineraryOf; t: T; closeLabel: string; look: ItineraryLook; headerClassName?: string;
}) {
  const { detail, closesAt, close, touched } = useDetail(of.item);
  if (detail?.view !== ITINERARY || of.itinerary.legs.length < 2) return null; // a change to draw, as the backend
  return (
    <CoverPanel title={t('trains.itinerary.title')} icon="train" closeLabel={closeLabel} closesAt={closesAt}
      onClose={close} onTouch={touched} headerClassName={headerClassName}>
      {look === 'cards' ? <LegCards itinerary={of.itinerary} t={t} /> : <Timeline itinerary={of.itinerary} t={t} />}
    </CoverPanel>
  );
}

function changeText(change: ItineraryChange, t: T): string {
  const at = t('trains.itinerary.change', { station: change.station });
  return change.waitMin === null ? at : `${at} · ${t('trains.itinerary.wait', { n: change.waitMin })}`;
}

/** The old kiosk's: a card per leg, the change a pill between two. */
export function LegCards({ itinerary, t }: { itinerary: Itinerary; t: T }) {
  return (
    <ol className={css.cards}>
      {itinerary.legs.map((leg, i) => (
        <li key={`${leg.trainNumber}-${i}`}>
          <div className={css.leg}>
            <p className={css.train}><Icon name="train" size={20} /><b>{leg.trainType}</b><span>{leg.trainNumber}</span></p>
            <p className={css.ends}>
              <span><strong>{leg.departure || '--:--'}</strong>{leg.origin}</span>
              <Icon name="next" size={20} />
              <span><strong>{leg.arrival || '--:--'}</strong>{leg.destination}</span>
            </p>
          </div>
          {itinerary.changes[i] && <p className={css.pill}><i aria-hidden />{changeText(itinerary.changes[i], t)}<i aria-hidden /></p>}
        </li>
      ))}
    </ol>
  );
}

/** One line down the stops: the time large, the station beside it, the train on the line between two stops. */
export function Timeline({ itinerary, t }: { itinerary: Itinerary; t: T }) {
  return (
    <ol className={css.timeline}>
      {itinerary.legs.map((leg, i) => (
        <Fragment key={`${leg.trainNumber}-${i}`}>
          <li className={css.stop}>
            <strong>{leg.departure || '--:--'}</strong><span>{leg.origin}</span>
          </li>
          <li className={css.ride}><span>{leg.trainType} {leg.trainNumber}</span></li>
          <li className={css.stop}>
            <strong>{leg.arrival || '--:--'}</strong><span>{leg.destination}</span>
          </li>
          {itinerary.changes[i] && <li className={css.wait}><span>{changeText(itinerary.changes[i], t)}</span></li>}
        </Fragment>
      ))}
    </ol>
  );
}
